import React, { useEffect, useState } from 'react';
import {
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  TextField,
  CircularProgress,
  Alert,
} from '@mui/material';
import { X } from 'lucide-react';
import { emptyLocationTypeForm, LocationType, LocationTypeFormData } from '../../types/settingsLocationTypeModel';
import { updateLocationType, createLocationType } from '../../services/settingsLocationType.api';


interface Props {
  open: boolean;
  editItem: LocationType | null; // null = create mode
  onClose: () => void;
  onSuccess: () => void;
}

const TRAFFIC_LEVELS = [
  'Very High',
  'High',
  'Medium to High',
  'Medium',
  'Low to Medium',
  'Low',
];

const inputSx = {
  '& .MuiOutlinedInput-root': {
    borderRadius: '10px',
    fontFamily: 'DM Sans, sans-serif',
    fontSize: 13,
    '& fieldset': { borderColor: 'var(--border)' },
    '&:hover fieldset': { borderColor: 'var(--brand)' },
    '&.Mui-focused fieldset': { borderColor: 'var(--brand)', borderWidth: 1.5 },
  },
  '& .MuiInputLabel-root': {
    fontFamily: 'DM Sans, sans-serif',
    fontSize: 13,
    '&.Mui-focused': { color: 'var(--brand)' },
  },
};

const LocationTypeFormModal: React.FC<Props> = ({ open, editItem, onClose, onSuccess }) => {
  const isEdit = Boolean(editItem);
  const [form, setForm] = useState<LocationTypeFormData>(emptyLocationTypeForm());
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');

  useEffect(() => {
    if (open) {
      setError('');
      if (editItem) {
        setForm({
          name: editItem.name,
          trafficLevel: editItem.trafficLevel,
          commissionPercentage: String(editItem.commissionPercentage),
          description: editItem.description,
        });
      } else {
        setForm(emptyLocationTypeForm());
      }
    }
  }, [open, editItem]);

  const handleChange = (field: keyof LocationTypeFormData) => (
    e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>
  ) => {
    setForm(prev => ({ ...prev, [field]: e.target.value }));
    setError('');
  };

  const validate = (): string => {
    if (!form.name.trim()) return 'Name is required.';
    if (!form.trafficLevel.trim()) return 'Traffic level is required.';
    const commission = Number(form.commissionPercentage);
    if (isNaN(commission) || commission < 0 || commission > 100)
      return 'Commission must be between 0 and 100.';
    if (!form.description.trim()) return 'Description is required.';
    return '';
  };

  const handleSubmit = async () => {
    const err = validate();
    if (err) { setError(err); return; }

    setLoading(true);
    setError('');
    try {
      const payload = {
        name: form.name.trim(),
        trafficLevel: form.trafficLevel.trim(),
        commissionPercentage: Number(form.commissionPercentage),
        description: form.description.trim(),
      };
      if (isEdit && editItem) {
        await updateLocationType(editItem.id, payload);
      } else {
        await createLocationType(payload);
      }
      onSuccess();
      onClose();
    } catch (e: any) {
      setError(e.message || 'Something went wrong.');
    } finally {
      setLoading(false);
    }
  };

  return (
    <Dialog
      open={open}
      onClose={onClose}
      fullWidth
      maxWidth="sm"
      PaperProps={{
        sx: {
          borderRadius: '16px',
          fontFamily: 'DM Sans, sans-serif',
          boxShadow: '0 20px 60px rgba(30,20,10,0.15)',
        },
      }}
    >
      {/* Header */}
      <DialogTitle
        sx={{
          display: 'flex',
          justifyContent: 'space-between',
          alignItems: 'center',
          pb: 1,
          pt: 2.5,
          px: 3,
          borderBottom: '1px solid var(--border-light)',
        }}
      >
        <div>
          <div style={{ fontFamily: 'DM Sans', fontWeight: 700, fontSize: 16, color: 'var(--text-primary)' }}>
            {isEdit ? 'Edit Location Type' : 'Add Location Type'}
          </div>
          <div style={{ fontFamily: 'DM Sans', fontSize: 12, color: 'var(--text-muted)', marginTop: 2 }}>
            {isEdit ? 'Update the location type details below.' : 'Fill in the details for the new location type.'}
          </div>
        </div>
        <button
          onClick={onClose}
          style={{
            background: 'var(--bg-muted)',
            border: '1px solid var(--border)',
            borderRadius: 8,
            width: 32,
            height: 32,
            display: 'flex',
            alignItems: 'center',
            justifyContent: 'center',
            cursor: 'pointer',
            color: 'var(--text-muted)',
          }}
        >
          <X size={15} />
        </button>
      </DialogTitle>

      <DialogContent sx={{ px: 3, pt: 2.5, pb: 1 }}>
        {error && (
          <Alert
            severity="error"
            sx={{ mb: 2, fontFamily: 'DM Sans', fontSize: 13, borderRadius: '10px' }}
          >
            {error}
          </Alert>
        )}

        <div style={{ display: 'flex', flexDirection: 'column', gap: 18 }}>
          {/* Name */}
          <TextField
            label="Location Type Name"
            value={form.name}
            onChange={handleChange('name')}
            fullWidth
            size="small"
            sx={inputSx}
            placeholder="e.g. A+ Prime Location"
          />

          {/* Traffic Level */}
          <TextField
            label="Traffic Level"
            value={form.trafficLevel}
            onChange={handleChange('trafficLevel')}
            fullWidth
            size="small"
            sx={inputSx}
            select
            SelectProps={{ native: true }}
          >
            <option value="">Select traffic level</option>
            {TRAFFIC_LEVELS.map(t => (
              <option key={t} value={t}>{t}</option>
            ))}
          </TextField>

          {/* Commission */}
          <TextField
            label="Commission Percentage (%)"
            value={form.commissionPercentage}
            onChange={handleChange('commissionPercentage')}
            fullWidth
            size="small"
            sx={inputSx}
            type="number"
            inputProps={{ min: 0, max: 100, step: 0.5 }}
            placeholder="e.g. 15"
          />

          {/* Description */}
          <TextField
            label="Description"
            value={form.description}
            onChange={handleChange('description')}
            fullWidth
            size="small"
            sx={inputSx}
            multiline
            rows={3}
            placeholder="Describe this location type..."
          />
        </div>
      </DialogContent>

      <DialogActions sx={{ px: 3, py: 2.5, borderTop: '1px solid var(--border-light)', gap: 1.5 }}>
        <button
          onClick={onClose}
          disabled={loading}
          style={{
            background: 'var(--bg-muted)',
            border: '1px solid var(--border)',
            borderRadius: 10,
            padding: '9px 20px',
            fontFamily: 'DM Sans',
            fontWeight: 500,
            fontSize: 13,
            color: 'var(--text-secondary)',
            cursor: 'pointer',
          }}
        >
          Cancel
        </button>
        <button
          onClick={handleSubmit}
          disabled={loading}
          className="btn-brand"
          style={{ padding: '9px 22px', fontSize: 13, minWidth: 110, display: 'flex', alignItems: 'center', gap: 8 }}
        >
          {loading && <CircularProgress size={13} sx={{ color: '#fff' }} />}
          {loading ? 'Saving...' : isEdit ? 'Update' : 'Create'}
        </button>
      </DialogActions>
    </Dialog>
  );
};

export default LocationTypeFormModal;